import { useRef, useState } from "react";
import { Upload, X, FileText, Loader2, Image as ImageIcon } from "lucide-react";
import { API, getToken } from "@/lib/api";

// Datei-Upload-Feld:
// - Klick öffnet File-Picker
// - Drag & Drop
// - Bei Erfolg wird `value` (URL) via onChange gesetzt
// - Wenn `value` schon eine URL ist → Vorschau-Pill mit "Ersetzen" / "Entfernen"
//
// accept: "application/pdf" oder "image/*" – Standard: PDF
export default function FileUpload({ value, onChange, accept = "application/pdf,.pdf", label = "PDF hochladen", testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/admin/uploads`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "Upload fehlgeschlagen");
      onChange(j.url);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const filename = value ? value.split("/").pop() : "";
  const isImage = /\.(png|jpe?g|webp)$/i.test(value || "");

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid={testid ? `${testid}-input` : "fileupload-input"}
      />

      {value ? (
        <div className="flex items-center gap-2 sm:gap-3 px-3 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 min-w-0">
          {isImage ? (
            <img src={value} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center text-emerald-300 shrink-0">
              <FileText className="w-5 h-5" />
            </div>
          )}
          <div className="flex-1 min-w-0 overflow-hidden">
            <a href={value} target="_blank" rel="noopener noreferrer"
              className="text-sm text-emerald-200 hover:text-emerald-100 truncate block" data-testid={testid ? `${testid}-link` : undefined}>
              {filename}
            </a>
            <div className="text-[10px] text-slate-400 truncate">{value}</div>
          </div>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
            data-testid={testid ? `${testid}-replace` : undefined}
            className="shrink-0 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold bg-white/10 hover:bg-white/20 text-white transition disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Ersetzen"}
          </button>
          <button type="button" onClick={() => onChange("")} disabled={busy}
            data-testid={testid ? `${testid}-clear` : undefined}
            className="shrink-0 w-8 h-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 flex items-center justify-center transition disabled:opacity-50">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          disabled={busy}
          data-testid={testid ? `${testid}-trigger` : "fileupload-trigger"}
          className={`w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl border-2 border-dashed transition cursor-pointer ${
            drag ? "border-emerald-400 bg-emerald-500/10" : "border-white/15 bg-slate-800/40 hover:border-emerald-400/50 hover:bg-slate-800/60"
          } disabled:opacity-50`}>
          {busy ? (
            <><Loader2 className="w-4 h-4 animate-spin text-emerald-400" /><span className="text-sm text-slate-300">Wird hochgeladen …</span></>
          ) : (
            <>
              {accept.includes("image") ? <ImageIcon className="w-4 h-4 text-emerald-400" /> : <Upload className="w-4 h-4 text-emerald-400" />}
              <span className="text-sm text-slate-300">{label}</span>
              <span className="text-[11px] text-slate-500">· oder hierher ziehen</span>
            </>
          )}
        </button>
      )}

      {err && <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid={testid ? `${testid}-error` : undefined}>{err}</div>}

      {/* Manuelle URL-Alternative */}
      <details className="text-[11px] text-slate-500">
        <summary className="cursor-pointer hover:text-slate-300">Oder URL manuell eingeben</summary>
        <input type="url" value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="mt-2 w-full px-3 py-2 rounded-lg bg-slate-800/60 border border-white/10 text-white text-sm focus:border-emerald-400 focus:outline-none" />
      </details>
    </div>
  );
}
